import { useQuery } from '@tanstack/react-query';
import { ChildrenService } from '@/api';
import type { children_audit_ChildrenAuditRecord } from '@/api';
import { createAuditHistoryHook, createInvalidateAuditQueries, createAuditQueryKeys } from './useGenericAudit';

// Create children audit history hook using the generic factory
export const useChildrenAuditHistory = createAuditHistoryHook<children_audit_ChildrenAuditRecord>({
  entityType: 'children',
  apiFunction: (entityId: string, page: number, limit: number) => 
    ChildrenService.getApiV1ChildrenHistory(entityId, page, limit),
});

// Create invalidation function for children audit queries
export const invalidateChildrenAuditQueries = createInvalidateAuditQueries('children');

// Create specific invalidation function for complete audit queries
export function invalidateChildrenCompleteAuditQueries(queryClient: import('@tanstack/react-query').QueryClient, childId: string) {
  // Invalidate standard audit queries
  invalidateChildrenAuditQueries(queryClient, childId);
  // Also invalidate complete audit queries with all pagination variations
  queryClient.invalidateQueries({ 
    queryKey: [...childrenAuditKeys.history(childId), 'complete'],
    exact: false // This will match all queries that start with this key, including pagination params
  });
}

// Additional hooks for field-specific and recent changes functionality
// These follow the same pattern but use different API endpoints

// Get query keys for children
const childrenAuditKeys = createAuditQueryKeys('children');

// Hook to fetch field-specific history for a child
export function useChildFieldHistory(
  childId: string,
  fieldName: string,
  options?: {
    enabled?: boolean;
  }
) {
  const { enabled = true } = options || {};

  return useQuery<children_audit_ChildrenAuditRecord[]>({
    queryKey: childrenAuditKeys.fieldHistory(childId, fieldName),
    queryFn: async () => {
      const result = await ChildrenService.getApiV1ChildrenHistoryField(childId, fieldName);
      return result;
    },
    enabled: enabled && !!childId && !!fieldName,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch recent changes for a child
export function useChildRecentChanges(
  childId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
) {
  const { enabled = true, refetchInterval } = options || {};

  return useQuery<children_audit_ChildrenAuditRecord[]>({
    queryKey: childrenAuditKeys.recentChanges(childId),
    queryFn: () => ChildrenService.getApiV1ChildrenHistoryRecent(childId),
    enabled: enabled && !!childId,
    staleTime: 60000, // 1 minute
    refetchInterval, // Optional auto-refresh for recent changes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch complete audit history including related entities
export function useChildrenCompleteAudit(
  childId: string,
  options?: {
    page?: number;
    limit?: number;
    enabled?: boolean;
    isTabActive?: boolean;
  }
) {
  const { page = 1, limit = 20, enabled = true } = options || {};
  const isTabActive = options?.isTabActive;

  return useQuery({
    queryKey: [...childrenAuditKeys.history(childId), 'complete', page, limit],
    queryFn: async () => {
      const response = await ChildrenService.getApiV1ChildrenAuditComplete(childId, page, limit);
      return response;
    },
    enabled: enabled && !!childId && (isTabActive === undefined || isTabActive),
    staleTime: 0, // Always considered stale to ensure fresh data on tab switch
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (renamed from cacheTime)
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Always refetch when component mounts
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}